import React from 'react';
import {
    Avatar,
    Box,
    Typography,
    Theme,
    Card,
    CardContent,
    Button,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { CapitalizeNames } from 'src/utils/capitalize';
import getInitials from 'src/utils/getInitials';
import { makeStyles } from '@material-ui/styles';
import moment from 'moment';
import { getMessage } from 'src/components/Tasks/List/utils/getMessage';
import { ITaskCard } from './types';
import { Label } from 'src/components/Tags';
import { getTaskActionsColor } from 'src/utils/colors';
import { ArrowRight } from 'react-feather';
import {
    CREATE_PERMISSION,
    LEAD_TASKS_CALENDAR_AUTH,
} from 'src/constants/permissions';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import useAuth from 'src/hooks/useAuth';
import AssistantGeneratedBadge from 'src/components/AssistantGeneratedBadge';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        height: '100%',
    },
    avatar: {
        width: 48,
        height: 48,
    },
    containerComment: {
        display: 'flex',
        gap: theme.spacing(2),
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    followUpButton: {
        fontSize: 14,
        height: 35,
        whiteSpace: 'nowrap',
    },
}));

export const TaskCard = ({ comment, setOpen, isFormOpen }: ITaskCard) => {
    const { t } = useTranslation();
    const { user, comment: message, reschedule, action } = comment;
    const classes = useStyles();
    const { user: userAuth } = useAuth();

    const handleOpen = () => setOpen(true);

    const canCreateTask = isPermissionGranted({
        permissions: userAuth.permissions || {},
        permission: LEAD_TASKS_CALENDAR_AUTH,
        crud: CREATE_PERMISSION,
    });

    return (
        <Card className={classes.root} elevation={0}>
            <CardContent className={classes.content}>
                <Box className={classes.containerComment}>
                    <Avatar className={classes.avatar} src={user.image}>
                        {getInitials(user.name)}
                    </Avatar>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gridGap={5}
                        flexGrow={1}
                    >
                        <Typography variant="h4" color="textPrimary">
                            {CapitalizeNames(user.name)}
                        </Typography>
                        {comment.isAssistantGenerated && (
                            <AssistantGeneratedBadge />
                        )}
                        <Typography color="textSecondary" variant="caption">
                            {t('Comments.Next')}:{' '}
                            <Label color="cyan">
                                <p style={{ fontSize: 12 }}>
                                    {moment(reschedule).format(
                                        'DD MMMM YYYY - hh:mm a'
                                    )}
                                </p>
                            </Label>
                        </Typography>
                    </Box>
                    {!isFormOpen && canCreateTask && (
                        <Button
                            endIcon={<ArrowRight />}
                            onClick={handleOpen}
                            variant="text"
                            className={classes.followUpButton}
                        >
                            {t('Buttons.FollowUp')}
                        </Button>
                    )}
                </Box>
                <Box display="flex" gridGap={5}>
                    {action.map((element) => (
                        <Label color={getTaskActionsColor(element)}>
                            <p>{t(`KeysComment.${element}`)}</p>
                        </Label>
                    ))}
                </Box>
                <Typography variant="body1" color="textPrimary">
                    {getMessage(message || '', t)}
                </Typography>
            </CardContent>
        </Card>
    );
};
